// @flow

import { Intent } from '@blueprintjs/core';
import * as ACTIONS from '../constants/actions';
import * as AuthAPI from '../services/auth-api';
import { errorToast } from '../utils/toaster';
import { CAN_VIEW_SETTINGS_PAGE } from '../constants/permissions';
import * as UserListModel from '../models/user-list';
import * as RoleModel from '../models/role';
import type { UserStatusKey, EmbeddedIncludeKey } from '../reducers/users';

type FetchOptions = { include?: Array<EmbeddedIncludeKey> };

type SingleIncludeOutcome = {
  ok: boolean,
  include: EmbeddedIncludeKey,
  users: UserListModel.userT[],
  err: any,
};

const DEFAULT_INCLUDES: Array<EmbeddedIncludeKey> = ['preferences', 'roles'];

const orgIdFromState = (state: any): ?string => {
  const id =
    state && state.user && state.user.model && state.user.model.organisationId;
  if (typeof id !== 'string' || id.length === 0) return null;
  return id;
};

const errorStatus = (err: any): ?number =>
  err && err.response && typeof err.response.status === 'number'
    ? err.response.status
    : null;

// ── Action creators ─────────────────────────────────────────────

export const usersFetchRequest = (status: UserStatusKey) => ({
  type: ACTIONS.USERS_FETCH_REQUEST,
  payload: { status },
});

export const usersFetchSuccess = (
  status: UserStatusKey,
  users: UserListModel.userT[],
  embeddedIncludes: $ReadOnlyArray<EmbeddedIncludeKey>,
) => ({
  type: ACTIONS.USERS_FETCH_SUCCESS,
  payload: { status, users, embeddedIncludes },
});

export const usersFetchError = (status: UserStatusKey, error: string) => ({
  type: ACTIONS.USERS_FETCH_ERROR,
  payload: { status, error },
});

export const userRoleAdded = (userId: number, role: UserListModel.roleDataT) => ({
  type: ACTIONS.USER_ROLE_ADDED,
  payload: { userId, role },
});

export const userRoleRemoved = (userId: number, roleId: number) => ({
  type: ACTIONS.USER_ROLE_REMOVED,
  payload: { userId, roleId },
});

export const userPreferenceCreated = (userId: number, preference: Object) => ({
  type: ACTIONS.USER_PREFERENCE_CREATED,
  payload: { userId, preference },
});

export const userPreferenceUpdated = (userId: number, preference: Object) => ({
  type: ACTIONS.USER_PREFERENCE_UPDATED,
  payload: { userId, preference },
});

export const userPreferenceDeleted = (userId: number, preferenceId: number) => ({
  type: ACTIONS.USER_PREFERENCE_DELETED,
  payload: { userId, preferenceId },
});

export const userRemoved = (userId: number) => ({
  type: ACTIONS.USER_REMOVED,
  payload: { userId },
});

export const usersReset = () => ({
  type: ACTIONS.USERS_RESET,
  payload: {},
});

// ── Internal helpers ────────────────────────────────────────────

// The `?include=roles` endpoint embeds UserRole join rows shaped
// `{ id: <joinId>, userId, orgId, roleId, roleName, … }`. The picker and the
// rest of the app expect `{ id: <roleId>, name }`, so normalise on entry and
// let only the wire boundary care about the difference. The client-friendly
// fields are carried through untouched — they are what the picker falls back
// to when the roles catalogue is unavailable.
const normaliseRole = (row: any): UserListModel.roleDataT => ({
  auraAdminOnly: false,
  createdAt: (row && row.createdAt) || '',
  updatedAt: (row && row.updatedAt) || '',
  id: row && row.roleId != null ? row.roleId : row && row.id,
  name: (row && (row.roleName || row.name)) || '',
  defaultRole: row && row.defaultRole,
  clientAvailable: row && row.clientAvailable,
  clientFriendlyRoleName: row && row.clientFriendlyRoleName,
  clientFriendlyDescription: row && row.clientFriendlyDescription,
});

const decorateUsers = (
  rawList: any,
  status: UserStatusKey,
): UserListModel.userT[] => {
  const list = Array.isArray(rawList) ? rawList : [];
  return list
    .filter(
      (u) => u && typeof u.email === 'string' && !u.email.includes('@auravision.ai'),
    )
    .map((u) => ({
      ...u,
      userListType: status,
      roles: Array.isArray(u.roles) ? u.roles.map(normaliseRole) : u.roles,
    }));
};

const mergeUserLists = (
  primary: UserListModel.userT[],
  secondary: UserListModel.userT[],
): UserListModel.userT[] => {
  const byId: { [number]: UserListModel.userT } = {};
  primary.forEach((u) => {
    if (u && typeof u.id === 'number') byId[u.id] = u;
  });
  secondary.forEach((u) => {
    if (!u || typeof u.id !== 'number') return;
    byId[u.id] = byId[u.id] ? { ...byId[u.id], ...u } : u;
  });
  // Preserve primary's order, append any extras from secondary.
  const seen: { [number]: true } = {};
  const out: UserListModel.userT[] = [];
  primary.forEach((u) => {
    if (u && typeof u.id === 'number' && !seen[u.id]) {
      seen[u.id] = true;
      out.push(byId[u.id]);
    }
  });
  secondary.forEach((u) => {
    if (u && typeof u.id === 'number' && !seen[u.id]) {
      seen[u.id] = true;
      out.push(byId[u.id]);
    }
  });
  return out;
};

const callWithIncludes = async (
  orgId: string,
  status: UserStatusKey,
  include?: Array<EmbeddedIncludeKey>,
): Promise<UserListModel.userT[]> => {
  const res = await AuthAPI.getUsers({ orgId, status, include });
  const data = (res && res.data) || [];
  return decorateUsers(data, status);
};

// ── Thunks ──────────────────────────────────────────────────────

export const fetchUsersForStatus =
  (status: UserStatusKey, options?: FetchOptions) =>
  async (dispatch: (any) => any, getState: () => any): Promise<void> => {
    dispatch(usersFetchRequest(status));

    const orgId = orgIdFromState(getState());
    if (!orgId) {
      dispatch(usersFetchError(status, 'no-org'));
      return;
    }

    const requestedIncludes: Array<EmbeddedIncludeKey> =
      options && Array.isArray(options.include) ? options.include : DEFAULT_INCLUDES;

    try {
      const users = await callWithIncludes(orgId, status, requestedIncludes);
      dispatch(usersFetchSuccess(status, users, requestedIncludes));
      return;
    } catch (err) {
      if (errorStatus(err) !== 403) {
        dispatch(
          usersFetchError(
            status,
            (err && err.message) || 'unknown',
          ),
        );
        return;
      }
    }

    // Per-include 403 fallback.
    if (requestedIncludes.length > 1) {
      const runSingleInclude = async (
        inc: EmbeddedIncludeKey,
      ): Promise<SingleIncludeOutcome> => {
        try {
          const users = await callWithIncludes(orgId, status, [inc]);
          return { ok: true, include: inc, users, err: null };
        } catch (e) {
          return { ok: false, include: inc, users: [], err: e };
        }
      };

      const [includeA, includeB] = requestedIncludes;
      const settled = await Promise.all([
        runSingleInclude(includeA),
        runSingleInclude(includeB),
      ]);

      const successes = settled.filter((s) => s.ok);
      const non403Failure = settled.find(
        (s) => !s.ok && errorStatus(s.err) !== 403,
      );

      if (non403Failure && successes.length === 0) {
        dispatch(
          usersFetchError(
            status,
            (non403Failure.err && non403Failure.err.message) || 'unknown',
          ),
        );
        return;
      }

      if (successes.length === 2) {
        const merged = mergeUserLists(successes[0].users, successes[1].users);
        dispatch(usersFetchSuccess(status, merged, requestedIncludes));
        return;
      }

      if (successes.length === 1) {
        const only = successes[0];
        dispatch(usersFetchSuccess(status, only.users, [only.include]));
        return;
      }

      // Both single-include retries 403'd → fall through to no-include call.
    }

    try {
      const users = await callWithIncludes(orgId, status, undefined);
      dispatch(usersFetchSuccess(status, users, []));
    } catch (err) {
      if (errorStatus(err) === 403) {
        dispatch(usersFetchError(status, 'no-permission'));
        return;
      }
      dispatch(
        usersFetchError(status, (err && err.message) || 'unknown'),
      );
    }
  };

export const fetchAllUsersWithIncludes =
  (options?: FetchOptions) =>
  async (dispatch: (any) => any, getState: () => any): Promise<void> => {
    const state = getState();
    const canView =
      state &&
      state.user &&
      state.user.model &&
      state.user.model.permissions &&
      state.user.model.permissions[CAN_VIEW_SETTINGS_PAGE];
    if (!canView) {
      dispatch(usersFetchError('normal', 'no-permission'));
      dispatch(usersFetchError('invited', 'no-permission'));
      dispatch(usersFetchError('adopted', 'no-permission'));
      return;
    }
    await Promise.all([
      dispatch(fetchUsersForStatus('normal', options)),
      dispatch(fetchUsersForStatus('invited', options)),
      dispatch(fetchUsersForStatus('adopted', options)),
    ]);
  };

export const addRoleToUser =
  (userId: number, role: RoleModel.catalogueRoleT) =>
  async (dispatch: (any) => any, getState: () => any): Promise<void> => {
    const orgId = orgIdFromState(getState());
    if (!orgId) {
      errorToast({
        message: "User role couldn't be changed.",
        intent: Intent.WARNING,
      });
      return;
    }
    try {
      const response = await AuthAPI.createUserRole(userId, role.id, orgId);
      const created = (response && response.data) || response || {};
      // The optimistic row is built from the catalogue role the picker passed
      // up, with the join row's timestamps preferred when the API returns them.
      const addedRole: UserListModel.roleDataT = {
        auraAdminOnly: false,
        createdAt: (created && created.createdAt) || role.createdAt || '',
        updatedAt: (created && created.updatedAt) || role.updatedAt || '',
        id: role.id,
        name: role.name || String(role.id),
        defaultRole: role.defaultRole,
        clientAvailable: role.clientAvailable,
        clientFriendlyRoleName: role.clientFriendlyRoleName,
        clientFriendlyDescription: role.clientFriendlyDescription,
      };
      dispatch(userRoleAdded(userId, addedRole));
      errorToast({
        message: 'User role updated successfully',
        intent: Intent.SUCCESS,
      });
    } catch (e) {
      errorToast({
        message: "User role couldn't be changed.",
        intent: Intent.WARNING,
      });
    }
  };

export const removeRoleFromUser =
  (userId: number, roleId: number) =>
  async (dispatch: (any) => any, getState: () => any): Promise<void> => {
    const orgId = orgIdFromState(getState());
    if (!orgId) {
      errorToast({
        message: "User role couldn't be changed.",
        intent: Intent.WARNING,
      });
      return;
    }
    try {
      await AuthAPI.deleteUserRole(userId, roleId, orgId);
      dispatch(userRoleRemoved(userId, roleId));
      errorToast({
        message: 'User role updated successfully',
        intent: Intent.SUCCESS,
      });
    } catch (e) {
      errorToast({
        message: "User role couldn't be changed.",
        intent: Intent.WARNING,
      });
    }
  };

export const createUserPreference =
  (
    userId: number,
    body: { orgId: string, preferenceType: string, preferenceValue: string },
  ) =>
  async (dispatch: (any) => any): Promise<?Object> => {
    try {
      const response = await AuthAPI.createUserPreference(userId, (body: any));
      const preference = (response && response.data) || response;
      if (preference) dispatch(userPreferenceCreated(userId, preference));
      return preference;
    } catch (e) {
      return null;
    }
  };

export const updateUserPreference =
  (
    userId: number,
    preferenceId: number,
    body: { preferenceValue: string },
  ) =>
  async (dispatch: (any) => any): Promise<?Object> => {
    try {
      const response = await AuthAPI.updateUserPreference(
        userId,
        preferenceId,
        body,
      );
      const preference = (response && response.data) || response;
      if (preference) dispatch(userPreferenceUpdated(userId, preference));
      return preference;
    } catch (e) {
      return null;
    }
  };

export const deleteUserPreference =
  (userId: number, preferenceId: number) =>
  async (dispatch: (any) => any): Promise<boolean> => {
    try {
      await AuthAPI.deleteUserPreference(userId, preferenceId);
      dispatch(userPreferenceDeleted(userId, preferenceId));
      return true;
    } catch (e) {
      return false;
    }
  };

export const removeUser =
  (userId: number, userType: UserStatusKey) =>
  async (dispatch: (any) => any, getState: () => any): Promise<void> => {
    try {
      if (userType === 'normal' || userType === 'invited') {
        await AuthAPI.deleteUser(userId);
      } else {
        const orgId = orgIdFromState(getState());
        if (!orgId) throw new Error('no-org');
        await AuthAPI.deleteAdoptedUser(userId, orgId);
      }
      dispatch(userRemoved(userId));
      errorToast({
        message: 'User removed successfully.',
        intent: Intent.SUCCESS,
      });
    } catch (e) {
      errorToast({
        message: "User coudn't be removed.",
        intent: Intent.WARNING,
      });
    }
  };
